import { useEffect, useRef, useState } from 'react'
import { NavLink, useNavigate, useLocation } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import {
  LayoutDashboard,
  Building2,
  FolderKanban,
  CheckSquare,
  Calendar,
  FileEdit,
  Megaphone,
  TrendingUp,
  Sparkles,
  Settings,
  ChevronLeft,
  ChevronRight,
  LogOut,
  User,
  BarChart3,
  Newspaper,
  Palette,
  GraduationCap,
  KeyRound,
  Users,
  ShieldCheck,
  Receipt,
  FileSpreadsheet,
  ListOrdered,
  Truck,
  CalendarCheck,
  BookOpen,
  Mail,
  Database,
  Share2,
  Film,
  ClipboardCheck,
  Eye,
  StickyNote,
  ShoppingBag,
  ListChecks,
  Package,
  UserCheck,
  Inbox,
  Camera,
  HeartPulse,
  Send,
} from 'lucide-react'
import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import { useUIStore } from '@/stores/uiStore'
import { useAuthStore } from '@/stores/authStore'
import { useProjectContextStore } from '@/stores/projectContextStore'
import { useInstanceStore, isInstanceComplete, type InstanceInfo } from '@/stores/instanceStore'
import { projectsApi, emailApi } from '@/services/api'
import { cn } from '@/lib/utils'
import type { LucideIcon } from 'lucide-react'
import type { ModuleType } from '@/types'

interface NavItem {
  label: string
  icon: LucideIcon
  href: string
  minLevel?: number
  moduleType?: string
  badge?: number
}

interface NavGroup {
  label: string
  items: NavItem[]
  minLevel?: number
}

const NAV_GROUPS: NavGroup[] = [
  {
    label: 'Principale',
    items: [
      { label: 'Dashboard', icon: LayoutDashboard, href: '/', minLevel: 5 },
      { label: 'Calendario', icon: Calendar, href: '/calendar', minLevel: 5 },
    ],
  },
  {
    label: 'Gestione',
    items: [
      { label: 'Clienti', icon: Building2, href: '/clients', minLevel: 3 },
      { label: 'Progetti', icon: FolderKanban, href: '/projects', minLevel: 5 },
      { label: 'Task', icon: CheckSquare, href: '/tasks', minLevel: 5 },
      { label: 'Preventivi', icon: FileSpreadsheet, href: '/preventivi', minLevel: 3 },
      { label: 'Collaboratori esterni', icon: Camera, href: '/collaboratori-esterni', minLevel: 2 },
      { label: 'Fornitori', icon: Truck, href: '/suppliers', minLevel: 2 },
      { label: 'Team', icon: Users, href: '/team', minLevel: 3 },
      { label: 'Review Contenuti', icon: Eye, href: '/content-review', minLevel: 2 },
      { label: 'Contabilità', icon: Receipt, href: '/accounting', minLevel: 2 },
    ],
  },
  {
    label: 'Moduli',
    items: [
      { label: 'PED', icon: FileEdit, href: '/ped', minLevel: 5, moduleType: 'SOCIAL' },
      { label: 'ADV', icon: Megaphone, href: '/adv', minLevel: 3, moduleType: 'ADV' },
      { label: 'PR', icon: Newspaper, href: '/pr', minLevel: 3, moduleType: 'PR' },
      { label: 'Branding', icon: Palette, href: '/branding', minLevel: 3, moduleType: 'BRANDING' },
      { label: 'Consulenza', icon: GraduationCap, href: '/consulenza', minLevel: 3, moduleType: 'CONSULENZA' },
      { label: 'Digital Hub', icon: Database, href: '/digital', minLevel: 3, moduleType: 'DIGITAL' },
      { label: 'Visual Production', icon: Film, href: '/visual-production', minLevel: 4, moduleType: 'VISUAL_PRODUCTION' },
      { label: 'Merchandising', icon: ShoppingBag, href: '/merchandising', minLevel: 4, moduleType: 'MERCHANDISING' },
    ],
  },
  {
    label: 'Strumenti',
    items: [
      { label: 'Calendario Social', icon: CalendarCheck, href: '/calendario-pubblicazioni', minLevel: 5 },
      { label: 'Email', icon: Mail, href: '/email', minLevel: 5 },
      { label: 'Note', icon: StickyNote, href: '/notes', minLevel: 5 },
      { label: 'To-Do', icon: ListChecks, href: '/todo', minLevel: 5 },
      { label: 'Magazzino', icon: Package, href: '/magazzino', minLevel: 4 },
      { label: 'Social Publisher', icon: Share2, href: '/social-publisher', minLevel: 3 },
      { label: 'Crescita Social', icon: TrendingUp, href: '/crescita-social', minLevel: 4 },
      { label: 'Richieste Sito', icon: Inbox, href: '/richieste', minLevel: 3 },
      { label: 'AI Assistant', icon: Sparkles, href: '/ai', minLevel: 5 },
      { label: 'Knowledge Base', icon: BookOpen, href: '/knowledge-base', minLevel: 3 },
      { label: 'Report', icon: BarChart3, href: '/reports', minLevel: 5 },
      { label: 'Collega Telegram', icon: Send, href: '/settings/telegram', minLevel: 5 },
    ],
  },
  {
    label: 'Amministrazione',
    minLevel: 2,
    items: [
      { label: 'Utenti', icon: Users, href: '/settings/users', minLevel: 2 },
      { label: 'Accessi Guest', icon: UserCheck, href: '/settings/guests', minLevel: 2 },
      { label: 'Catalogo Servizi', icon: ListOrdered, href: '/settings/catalog', minLevel: 2 },
      { label: 'Audit Trail', icon: ShieldCheck, href: '/settings/audit', minLevel: 2 },
      { label: 'Salute AI', icon: HeartPulse, href: '/settings/ai-health', minLevel: 2 },
      { label: 'Impostazioni', icon: Settings, href: '/settings', minLevel: 5 },
    ],
  },
]

// Istanza non completa (es. seconda istanza PROXIMA, vedi stores/instanceStore.ts):
// un modulo attivo → la sua unica voce di navigazione.
const MODULE_NAV_ITEMS: Record<string, NavItem> = {
  contabilita: { label: 'Contabilità', icon: Receipt, href: '/accounting' },
  email: { label: 'Email', icon: Mail, href: '/email' },
}

function NavItemLink({
  item,
  collapsed,
  onNavigate,
  projectId,
}: {
  item: NavItem
  collapsed: boolean
  onNavigate?: (() => void) | undefined
  projectId?: string | undefined
}) {
  // Append ?projectId= for module links when a project is active
  const to = item.moduleType && projectId
    ? `${item.href}?projectId=${projectId}`
    : item.href

  return (
    <NavLink
      to={to}
      end={item.href === '/'}
      onClick={onNavigate}
      className={({ isActive }) =>
        cn(
          'flex items-center gap-3 px-2.5 py-2.5 rounded-md text-sm font-medium transition-colors min-h-[44px]',
          'group relative',
          isActive
            ? 'bg-white dark:bg-slate-800 text-slate-900 shadow-card'
            : 'text-slate-500 hover:bg-white dark:hover:bg-slate-800 hover:text-slate-900 hover:shadow-card'
        )
      }
    >
      {({ isActive }) => (
        <>
          <item.icon
            className={cn(
              'w-4 h-4 flex-shrink-0',
              isActive ? 'text-accent-600' : 'text-slate-400 group-hover:text-slate-600'
            )}
          />
          {!collapsed && <span className="truncate">{item.label}</span>}

          {/* Unread badge */}
          {item.badge != null && item.badge > 0 && (
            <span className={cn(
              'flex items-center justify-center text-[10px] font-bold text-white bg-danger-500 rounded-full',
              collapsed ? 'absolute -top-0.5 -right-0.5 w-3.5 h-3.5' : 'ml-auto min-w-[18px] h-[18px] px-1'
            )}>
              {item.badge > 99 ? '99+' : item.badge}
            </span>
          )}

          {/* Tooltip when collapsed */}
          {collapsed && (
            <div className="absolute left-full ml-2 px-2 py-1 bg-slate-900 text-white text-xs rounded-md whitespace-nowrap opacity-0 pointer-events-none group-hover:opacity-100 transition-opacity z-50">
              {item.label}
              {item.badge != null && item.badge > 0 && ` (${item.badge})`}
            </div>
          )}
        </>
      )}
    </NavLink>
  )
}

function SectionDivider({ label, collapsed }: { label: string; collapsed: boolean }) {
  return (
    <div className="my-2 mx-2">
      {collapsed ? (
        <div className="border-t border-slate-200" />
      ) : (
        <div className="flex items-center gap-2">
          <div className="flex-1 border-t border-slate-200" />
          <span className="text-[10px] text-slate-400 uppercase tracking-wider font-medium whitespace-nowrap">
            {label}
          </span>
          <div className="flex-1 border-t border-slate-200" />
        </div>
      )}
    </div>
  )
}

interface SidebarContentProps {
  collapsed: boolean
  userLevel: number
  onNavigate?: (() => void) | undefined
}

const GUEST_NAV_ITEMS: NavItem[] = [
  { label: 'Piano Editoriale', icon: FileEdit, href: '/ped' },
  { label: 'Review Contenuti', icon: Eye, href: '/content-review' },
  { label: 'I Miei Progetti', icon: FolderKanban, href: '/projects' },
]

function GuestSidebarNav({ collapsed, onNavigate }: { collapsed: boolean; onNavigate?: (() => void) | undefined }) {
  return (
    <nav className="flex-1 px-2 py-3 overflow-y-auto">
      <div className="space-y-0.5">
        {GUEST_NAV_ITEMS.map((item) => (
          <NavItemLink
            key={item.href}
            item={item}
            collapsed={collapsed}
            onNavigate={onNavigate}
          />
        ))}
      </div>
    </nav>
  )
}

// Istanza non completa: solo le voci dei moduli attivi ('contabilita' → /accounting,
// 'email' → /email) più, per gli amministratori, Utenti e Impostazioni. Tutto il
// resto (gruppi Moduli/Strumenti, AI, Collega Telegram, ecc.) resta nascosto.
function ReducedSidebarNav({
  collapsed,
  userLevel,
  instance,
  onNavigate,
}: {
  collapsed: boolean
  userLevel: number
  instance: InstanceInfo
  onNavigate?: (() => void) | undefined
}) {
  const moduleItems: NavItem[] = Array.isArray(instance.modules)
    ? instance.modules
        .map((m) => MODULE_NAV_ITEMS[m])
        .filter((item): item is NavItem => Boolean(item))
    : []

  const adminItems: NavItem[] = [
    ...(userLevel <= 2 ? [{ label: 'Utenti', icon: Users, href: '/settings/users' }] : []),
    { label: 'Impostazioni', icon: Settings, href: '/settings' },
  ]

  return (
    <nav className="flex-1 px-2 py-3 overflow-y-auto">
      <div className="space-y-0.5">
        {moduleItems.map((item) => (
          <NavItemLink key={item.href} item={item} collapsed={collapsed} onNavigate={onNavigate} />
        ))}
      </div>
      <SectionDivider label="Amministrazione" collapsed={collapsed} />
      <div className="space-y-0.5">
        {adminItems.map((item) => (
          <NavItemLink key={item.href} item={item} collapsed={collapsed} onNavigate={onNavigate} />
        ))}
      </div>
    </nav>
  )
}

function SidebarNav({ collapsed, userLevel, onNavigate }: SidebarContentProps) {
  const location = useLocation()
  const user = useAuthStore((state) => state.user)
  const activeProject = useProjectContextStore((s) => s.activeProject)
  const instance = useInstanceStore((s) => s.instance)

  // Guest users get a simplified nav
  if (user?.isGuest) {
    return <GuestSidebarNav collapsed={collapsed} onNavigate={onNavigate} />
  }

  // Istanza non completa (es. PROXIMA): nav ridotta ai soli moduli attivi
  if (!isInstanceComplete(instance)) {
    return (
      <ReducedSidebarNav
        collapsed={collapsed}
        userLevel={userLevel}
        instance={instance}
        onNavigate={onNavigate}
      />
    )
  }
  const setActiveProject = useProjectContextStore((s) => s.setActiveProject)

  // Email unread count — polling ogni 30s
  const { data: emailUnreadRaw } = useQuery({
    queryKey: ['email-unread-sidebar'],
    queryFn: async () => {
      const res = await emailApi.getUnreadCount()
      return (res.data as { count: number }).count ?? 0
    },
    enabled: !!user,
    refetchInterval: 30_000,
    staleTime: 15_000,
  })

  // Badge sparisce quando sei sulla pagina email, riappare con nuove mail
  const isOnEmailPage = location.pathname === '/email'
  const emailSnapshotRef = useRef<number | null>(null)
  const [emailBadgeSeen, setEmailBadgeSeen] = useState(false)

  useEffect(() => {
    if (isOnEmailPage) {
      setEmailBadgeSeen(true)
      emailSnapshotRef.current = emailUnreadRaw ?? 0
    }
  }, [isOnEmailPage, emailUnreadRaw])

  useEffect(() => {
    if (!isOnEmailPage && emailSnapshotRef.current !== null && emailUnreadRaw !== undefined) {
      if (emailUnreadRaw > emailSnapshotRef.current) {
        setEmailBadgeSeen(false)
      }
    }
  }, [emailUnreadRaw, isOnEmailPage])

  const emailUnread = emailBadgeSeen ? 0 : (emailUnreadRaw ?? 0)

  // Auto-set progetto da ?projectId= (pagine modulo)
  const projectIdFromUrl = new URLSearchParams(location.search).get('projectId')

  const { data: fetchedProject } = useQuery({
    queryKey: ['project-context', projectIdFromUrl],
    queryFn: async () => {
      const res = await projectsApi.getById(projectIdFromUrl!)
      return res.data as { id: string; name: string; moduleTypes: ModuleType[] }
    },
    enabled: !!projectIdFromUrl && activeProject?.id !== projectIdFromUrl,
    staleTime: 10 * 60 * 1000,
  })

  const clearProject = useProjectContextStore((s) => s.clearProject)

  useEffect(() => {
    if (fetchedProject && activeProject?.id !== projectIdFromUrl) {
      setActiveProject({
        id: fetchedProject.id,
        name: fetchedProject.name,
        moduleTypes: fetchedProject.moduleTypes ?? [],
      })
    }
  }, [fetchedProject])

  // Pulisci contesto progetto quando non sei in una pagina progetto o modulo
  const isProjectPage = location.pathname.startsWith('/projects/')
  const hasProjectIdParam = Boolean(projectIdFromUrl)
  useEffect(() => {
    if (!isProjectPage && !hasProjectIdParam && activeProject) {
      clearProject()
    }
  }, [location.pathname, isProjectPage, hasProjectIdParam])

  // Permessi moduli utente (solo L3+)
  const userVisibleModules: ModuleType[] | null =
    userLevel >= 3 && user?.preferences?.visibleModules?.length
      ? user.preferences.visibleModules
      : null

  const visibleGroups = NAV_GROUPS.map((group) => ({
    ...group,
    items: group.items.map((item) => {
      if (item.href === '/email' && emailUnread) return { ...item, badge: emailUnread }
      return item
    }).filter((item) => {
      // Check livello utente
      if (item.minLevel && userLevel > item.minLevel) return false

      // Filtro moduli: solo in contesto progetto
      if (item.moduleType) {
        // Se non c'e' un progetto attivo, nascondi tutti i moduli
        if (!activeProject) return false

        // Mostra solo i moduli attivi nel progetto
        if (!activeProject.moduleTypes.includes(item.moduleType as ModuleType)) return false

        // L3+: intersezione con i moduli permessi all'utente
        if (userVisibleModules && !userVisibleModules.includes(item.moduleType as ModuleType)) return false
      }

      return true
    }),
  })).filter((group) => {
    if (group.minLevel && userLevel > group.minLevel) return false
    return group.items.length > 0
  })

  return (
    <nav className="flex-1 px-2 py-3 overflow-y-auto">
      {visibleGroups.map((group, groupIdx) => (
        <div key={group.label}>
          {groupIdx > 0 && (
            <SectionDivider label={group.label} collapsed={collapsed} />
          )}
          <div className="space-y-0.5">
            {group.items.map((item) => (
              <NavItemLink
                key={item.href}
                item={item}
                collapsed={collapsed}
                onNavigate={onNavigate}
                projectId={activeProject?.id}
              />
            ))}
          </div>
        </div>
      ))}
    </nav>
  )
}

export function Sidebar() {
  const sidebarCollapsed = useUIStore((state) => state.sidebarCollapsed)
  const sidebarMobileOpen = useUIStore((state) => state.sidebarMobileOpen)
  const toggleSidebar = useUIStore((state) => state.toggleSidebar)
  const setMobileSidebarOpen = useUIStore((state) => state.setMobileSidebarOpen)
  const user = useAuthStore((state) => state.user)
  const logout = useAuthStore((state) => state.logout)
  const navigate = useNavigate()
  const instance = useInstanceStore((state) => state.instance)
  const instanceComplete = isInstanceComplete(instance)

  const userInitials = user
    ? `${user.firstName.charAt(0)}${user.lastName.charAt(0)}`.toUpperCase()
    : 'U'

  const userLevel = user?.role?.level ?? 5

  // Logo + nome E-quipe con istanza completa; con istanza non completa (es.
  // PROXIMA) il nome dell'istanza e un'iniziale al posto del logo E-quipe.
  const BrandMark = ({ className }: { className?: string }) =>
    instanceComplete ? (
      <img src="/logo-equipe.png" alt="E-QUIPE" className={cn('w-7 h-7', className)} />
    ) : (
      <div className={cn('w-7 h-7 rounded-lg bg-primary-600 flex items-center justify-center', className)}>
        <span className="text-white text-xs font-bold">{instance.name.charAt(0).toUpperCase()}</span>
      </div>
    )

  const handleLogout = () => {
    logout()
    navigate('/login')
  }

  function closeMobile() {
    setMobileSidebarOpen(false)
  }

  const UserFooter = ({ collapsed }: { collapsed: boolean }) => (
    <div className="border-t border-slate-200 dark:border-slate-700 p-2 flex-shrink-0">
      <DropdownMenu.Root>
        <DropdownMenu.Trigger asChild>
          <button
            className={cn(
              'flex items-center gap-2.5 px-2 py-2 rounded-md w-full',
              'hover:bg-slate-200 transition-colors',
              collapsed ? 'justify-center' : ''
            )}
          >
            <div className="w-7 h-7 bg-accent-600 rounded-full flex items-center justify-center flex-shrink-0">
              <span className="text-white text-xs font-semibold">{userInitials}</span>
            </div>
            {!collapsed && (
              <div className="flex-1 min-w-0 text-left">
                <p className="text-xs font-medium text-slate-900 truncate">
                  {user ? `${user.firstName} ${user.lastName}` : 'Utente'}
                </p>
                <p className="text-xs text-slate-400 truncate capitalize">
                  {user?.role?.name?.toLowerCase().replace('_', ' ') ?? ''}
                </p>
              </div>
            )}
          </button>
        </DropdownMenu.Trigger>

        <DropdownMenu.Portal>
          <DropdownMenu.Content
            className={cn(
              'min-w-48 bg-white dark:bg-slate-800 rounded-lg shadow-dropdown border border-slate-200 dark:border-slate-700',
              'p-1 z-[60] animate-fade-in'
            )}
            side="top"
            align="start"
            sideOffset={4}
          >
            <div className="px-3 py-2 border-b border-slate-100 mb-1">
              <p className="text-sm font-medium text-slate-900">
                {user ? `${user.firstName} ${user.lastName}` : 'Utente'}
              </p>
              <p className="text-xs text-slate-400">{user?.email ?? ''}</p>
            </div>

            <DropdownMenu.Item
              onSelect={() => navigate('/settings')}
              className="flex items-center gap-2 px-3 py-2 text-sm text-slate-700 rounded-md cursor-pointer hover:bg-slate-50 outline-none"
            >
              <User className="w-3.5 h-3.5 text-slate-400" />
              Profilo
            </DropdownMenu.Item>

            <DropdownMenu.Item
              onSelect={() => navigate('/settings')}
              className="flex items-center gap-2 px-3 py-2 text-sm text-slate-700 rounded-md cursor-pointer hover:bg-slate-50 outline-none"
            >
              <Settings className="w-3.5 h-3.5 text-slate-400" />
              Impostazioni
            </DropdownMenu.Item>

            <DropdownMenu.Separator className="my-1 border-t border-slate-100" />

            <DropdownMenu.Item
              onSelect={handleLogout}
              className="flex items-center gap-2 px-3 py-2 text-sm text-danger-600 rounded-md cursor-pointer hover:bg-danger-50 outline-none"
            >
              <LogOut className="w-3.5 h-3.5" />
              Logout
            </DropdownMenu.Item>
          </DropdownMenu.Content>
        </DropdownMenu.Portal>
      </DropdownMenu.Root>
    </div>
  )

  return (
    <>
      {/* ========== DESKTOP SIDEBAR ========== */}
      <aside
        className={cn(
          'fixed top-0 left-0 h-full flex flex-col bg-slate-50 dark:bg-slate-900 border-r border-slate-200 dark:border-slate-700',
          'transition-all duration-300 ease-in-out z-20',
          'hidden md:flex',
          sidebarCollapsed ? 'w-16' : 'w-64'
        )}
      >
        {/* Logo + collapse button */}
        <div className="flex items-center justify-between h-14 px-3 border-b border-slate-200 dark:border-slate-700 flex-shrink-0">
          {sidebarCollapsed ? (
            <BrandMark className="mx-auto" />
          ) : (
            <>
              <div className="flex items-center gap-2 min-w-0">
                <BrandMark className="flex-shrink-0" />
                <span className="font-semibold text-slate-900 truncate text-sm">
                  {instance.name}
                </span>
              </div>
              <button
                onClick={toggleSidebar}
                className="p-1 rounded-md text-slate-400 hover:text-slate-600 hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
                aria-label="Comprimi sidebar"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
            </>
          )}
        </div>

        {/* Expand button when collapsed */}
        {sidebarCollapsed && (
          <button
            onClick={toggleSidebar}
            className="flex items-center justify-center h-8 mt-2 mx-1.5 rounded-md text-slate-400 hover:text-slate-600 hover:bg-slate-200 transition-colors"
            aria-label="Espandi sidebar"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        )}

        <SidebarNav collapsed={sidebarCollapsed} userLevel={userLevel} />
        <UserFooter collapsed={sidebarCollapsed} />
      </aside>

      {/* ========== MOBILE SIDEBAR (overlay) ========== */}
      {/* Backdrop */}
      {sidebarMobileOpen && (
        <div
          className="fixed inset-0 bg-black/40 z-30 md:hidden"
          onClick={closeMobile}
        />
      )}

      {/* Mobile drawer */}
      <aside
        className={cn(
          'fixed top-0 left-0 h-full w-[min(18rem,85vw)] flex flex-col bg-slate-50 dark:bg-slate-900 border-r border-slate-200 dark:border-slate-700',
          'transition-transform duration-300 ease-in-out z-40 md:hidden',
          'pb-[env(safe-area-inset-bottom)]',
          sidebarMobileOpen ? 'translate-x-0' : '-translate-x-full'
        )}
      >
        {/* Mobile header with logo + close */}
        <div className="flex items-center justify-between h-14 px-3 border-b border-slate-200 dark:border-slate-700 flex-shrink-0">
          <div className="flex items-center gap-2 min-w-0">
            <BrandMark className="flex-shrink-0" />
            <span className="font-semibold text-slate-900 truncate text-sm">
              {instance.name}
            </span>
          </div>
          <button
            onClick={closeMobile}
            className="p-2.5 rounded-md text-slate-400 hover:text-slate-600 hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center"
            aria-label="Chiudi menu"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
        </div>

        <SidebarNav collapsed={false} userLevel={userLevel} onNavigate={closeMobile} />
        <UserFooter collapsed={false} />
      </aside>
    </>
  )
}
